import { Breadcrumb, Button, Checkbox, Dropdown, Icon, Modal, Radio, Search, TextArea, TextInput } from "pepsico-ds";
import "./app-registeration.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from 'react-toastify';
import { useLoading } from "../../loader";
import appRegisterationService from "./app-registeration.service";

const AppRegisteration = () => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { showLoader, hideLoader } = useLoading();
    const [showModal, setShowModal] = useState(false);
    const [requestData, setRequestData] = useState(null);
    const [copyButtonText, setCopyButtonText] = useState(t('nonArExtension.copy'));
    const [applicationName, setApplicationName] = useState("");
    const [supportGroup, setSupportGroup] = useState("");
    const [businessPurpose, setBusinessPurpose] = useState("");
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [environment, setEnvironment] = useState([]);
    const [environments, setEnvironments] = useState([
        {
            id: 'PROD', displayText: t("app_registeration.production")
        },
        {
            id: 'NONPROD', displayText: t("app_registeration.non_production")
        }
    ]);
    const selectedEnvironmentRef = useRef([]);
    const [applicationType, setApplicationType] = useState([]);
    const [applicationTypes, setApplicationTypes] = useState([
        {
            id: 'WEB', displayText: t("app_registeration.web_application")
        },
        {
            id: 'SPA', displayText: t("app_registeration.single_page_application")
        },
        {
            id: 'PUBLIC_CLIENT', displayText: t("app_registeration.mobile_desktop_application")
        },
        {
            id: 'API', displayText: t("app_registeration.background_service_or_automation")
        },
        {
            id: 'API', displayText: t("app_registeration.api")
        }
    ]);
    const [signInAudience, setSignInAudience] = useState([]);
    const [signInAudiences, setSignInAudiences] = useState([
        {
            id: 'AzureADMyOrg', displayText: t("app_registeration.single_tenant_only_pepsico")
        },
        {
            id: 'AzureADMultipleOrgs', displayText: t("app_registeration.multiple_entra_id_tenants")
        },
        {
            id: 'AzureADandPersonalMicrosoftAccount', displayText: t("app_registeration.any_entra_id_tenant_personal_microsoft_accounts")
        },
        {
            id: 'PersonalMicrosoftAccount', displayText: t("app_registeration.personal_accounts_only")
        }
    ]);
    const selectedSignInAudienceRef = useRef([]);
    const [requestedApiPermission, setRequestedApiPermission] = useState([]);
    const [requestedApiPermissions, setRequestedApiPermissions] = useState([]);
    const [apiPermissionsIndex, setApiPermissionsIndex] = useState(1);
    const [disableBusinessOwnerSearch, setDisableBusinessOwnerSearch] = useState(false);
    const [businessOwners, setBusinessOwners] = useState([]);
    const [selectedBusinessOwner, setSelectedBusinessOwner] = useState(null);
    const [searchedBusinessOwnerQuery, setSearchedBusinessOwnerQuery] = useState("");
    const [disableTechnicalOwnerSearch, setDisableTechnicalOwnerSearch] = useState(false);
    const [searchedTechnicalOwnerQuery, setSearchedTechnicalOwnerQuery] = useState("");
    const [users, setUsers] = useState([]);
    const [technicalOwners, setTechnicalOwners] = useState([]);
    const [businessOwner, setBusinessOwner] = useState([]);

    useEffect(() => {
        // Create a MutationObserver to watch for attribute changes
        const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === "attributes" && mutation.attributeName === "data-lang") {
                const environments = [
                        {
                            id: 'PROD', displayText: t("app_registeration.production")
                        },
                        {
                            id: 'NONPROD', displayText: t("app_registeration.non_production")
                        }
                    ];
                const signInAudiences = [
                    {
                        id: 'AzureADMyOrg', displayText: t("app_registeration.single_tenant_only_pepsico")
                    },
                    {
                        id: 'AzureADMultipleOrgs', displayText: t("app_registeration.multiple_entra_id_tenants")
                    },
                    {
                        id: 'AzureADandPersonalMicrosoftAccount', displayText: t("app_registeration.any_entra_id_tenant_personal_microsoft_accounts")
                    },
                    {
                        id: 'PersonalMicrosoftAccount', displayText: t("app_registeration.personal_accounts_only")
                    }
                ]
                const prevSelectedEnvironment = [...selectedEnvironmentRef.current]?.[0];
                if(prevSelectedEnvironment?.id)setEnvironment(environments?.filter?.((o)=>o.id === prevSelectedEnvironment.id));
                const prevSelectedSignInAudience = [...selectedSignInAudienceRef.current]?.[0];
                if(prevSelectedSignInAudience?.id)setSignInAudience(signInAudiences?.filter?.((o)=>o.id === prevSelectedSignInAudience.id));
                setEnvironments(environments);
                setSignInAudiences(signInAudiences);
            }
        });
        });

        // Start observing the body element
        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ["data-lang"],
        });

        // Cleanup observer on unmount
        return () => {
        observer.disconnect();
        };
    }, []);

    function useDebouncedCallback(callback, delay = 1000) {
        const timerRef = useRef();

        const debounced = useCallback((...args) => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            timerRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        }, [callback, delay]);

        useEffect(() => {
            return () => {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                }
            };
        }, []);

        return debounced;
    }

    const debouncedSearchTechnicalOwners = useDebouncedCallback((q) => {
        if (q?.trim().length) {
            searchTechnicalOwners(q);
        } else {
            setUsers([]);
        }
    }, 1500);

    const debouncedSearchBusinessOwners = useDebouncedCallback((q) => {
        if (q?.trim().length) {
            searchBusinessOwners(q);
        } else {
            setBusinessOwners([]);
        }
    }, 1500);

    useEffect(()=>{
        (async()=>{
           const resp = await appRegisterationService.getPermissions();
           if(resp.length>0){
               setRequestedApiPermissions(resp?.map?.((d)=>({...d, displayText: d?.displayName, id: d.permissionId})));
           }
        })()
    },[])

    const clearUsers = () => {
        setUsers([]);
        setSearchedTechnicalOwnerQuery('');
    }
    
    const clearBusinessUsers = () => {
        setBusinessOwners([]);
        setSearchedBusinessOwnerQuery('');
    }

    const addToBusinessOwners = (val) => {
        if (!val?.label) return;

        const parts = String(val.label)?.split?.('-')?.map?.((x)=>x?.trim?.());
        const selectedGpid = parts[0];

        if (!selectedGpid) return;

        const memberToAdd = { ...val, id: selectedGpid };

        setBusinessOwner((prev) =>
            prev.some((item) => item.id === selectedGpid)
                ? prev
                : [...prev, memberToAdd]
        );
    };

    const addToTechnicalOwners = (val) => {
        if (!val?.label) return;

        const parts = String(val.label)?.split?.('-')?.map?.((x)=>x?.trim?.());
        const selectedGpid = parts[0];

        if (!selectedGpid) return;

        const memberToAdd = { ...val, id: selectedGpid };

        setTechnicalOwners((prev) =>
            prev.some((item) => item.id === selectedGpid)
                ? prev
                : [...prev, memberToAdd]
        );
    };

    const searchTechnicalOwners = async (text = searchedTechnicalOwnerQuery) => {
        if(/[^0-9]/.test(text)){
            toast.error(t("app_registeration.only_numbers_are_allowed"));
            return;
        }
        if (!text || text?.trimStart()?.length < 4 || text?.trimStart()?.length > 8) {
            toast.error(t("app_registeration.please_enter_at_least_4_or_max_8_characters_to_search_for_an_gpid"));
            return;
        }
        try {
            showLoader();
            setDisableTechnicalOwnerSearch(true);
            const payload = {
                gpid: text
            }
            const response = await appRegisterationService.searchUsers(payload);
            if (response?.status === "Failed" || response?.errorMessage) {
                toast.error(response?.errorMessage);
            } else {
                const users = Object.values(response?.userData || {})?.map?.((d) => `${d?.uid} - ${d?.cn}`);
                if (users?.length) {
                    const mapped = users.map((item) => ({
                        id: item,
                        label: item,
                    }));
                    setUsers(mapped);
                } else {
                    toast.error(t("app_registeration.no_user_found"));
                }
            }
            hideLoader();
            setDisableTechnicalOwnerSearch(false);
        } catch (error) {
            toast.error(error?.message)
            hideLoader();
            setDisableTechnicalOwnerSearch(false);
        } finally {
            hideLoader();
            setDisableTechnicalOwnerSearch(false);
        }
    };

    const searchBusinessOwners = async (text = searchedBusinessOwnerQuery) => {
        if(/[^0-9]/.test(text)){
            toast.error(t("app_registeration.only_numbers_are_allowed"));
            return;
        }
        if (!text || text?.trimStart()?.length < 4 || text?.trimStart()?.length > 8) {
            toast.error(t("app_registeration.please_enter_at_least_4_or_max_8_characters_to_search_for_an_gpid"));
            return;
        }
        setBusinessOwners([]);
        try {
            showLoader();
            setDisableBusinessOwnerSearch(true);
            const payload = {
                gpid: text
            }
            const response = await appRegisterationService.searchUsers(payload);
            if (response?.status === "Failed" || response?.errorMessage) {
                toast.error(response?.errorMessage);
            } else {
                const users = Object.values(response?.userData||{})?.map?.((d) => `${d?.uid} - ${d?.cn}`);
                if (users?.length) {
                    const mapped = users.map((item) => ({
                        id: item,
                        label: item,
                    }));
                    setBusinessOwners(mapped);
                } else {
                    toast.error(t("app_registeration.no_user_found"));
                }
            }
            hideLoader();
            setDisableBusinessOwnerSearch(false);
        } catch (error) {
            toast.error(error?.message)
            hideLoader();
            setDisableBusinessOwnerSearch(false);
        } finally {
            hideLoader();
            setDisableBusinessOwnerSearch(false);
        }
    };

    const closeModal = () => {
        setShowModal(false);
    }

    const onClickCopynumber = () => {
        if (requestData?.requestNumber) {
            navigator.clipboard.writeText(requestData?.requestNumber?.toString?.()).then(() => {
                setCopyButtonText(t('nonArExtension.copied'));
            }).catch(err => {
                console.error("Failed to copy text: ", err);
            });
        }
    };

    const onViewRequest = () => {
        const statePayload = { type: "requestNumber", value: requestData?.requestNumber };
        navigate("/audit-requests", { state: statePayload });
    }

    const disableSubmit = () => {
        return !isConfirmed || !applicationName  || !environment?.length || !businessOwner?.length || technicalOwners.length < 2 || !signInAudience?.length || !businessPurpose || (apiPermissionsIndex===0 && !requestedApiPermission?.length)
    }

    const resetFields = () => {
        setApplicationName("");
        setEnvironment([]);
        selectedEnvironmentRef.current = [];
        setSelectedBusinessOwner(null);
        setSupportGroup("");
        setTechnicalOwners([]);
        setBusinessOwner([]);
        setApplicationType([]);
        setSignInAudience([]);
        selectedSignInAudienceRef.current = [];
        setApiPermissionsIndex(0);
        setRequestedApiPermission([]);
        setBusinessPurpose("");
        setIsConfirmed(false);
        setSearchedBusinessOwnerQuery("");
        setBusinessOwners([]);
        setUsers([]);
    }

    const handleSubmit = async () => {
        try {
            showLoader();
            const payload = {
                applicationName: applicationName,
                environment: environment?.[0]?.id,
                businessOwnerGpid: businessOwner?.[0]?.id,
                supportGroup: supportGroup,
                technicalOwnerGpids: technicalOwners?.map?.((t)=>t?.id),
                // applicationType: applicationType?.[0]?.id,
                signInAudience: signInAudience?.[0]?.id,
                ...(apiPermissionsIndex === 0 ? {apiPermissions: requestedApiPermission?.map?.((r)=>({resourceAppId: r?.resourceAppId, permissionId: r?.permissionId, type: r?.permissionType, name: r?.permissionName}))} : {apiPermissions:[]}),
                businessPurpose: businessPurpose
            }
            
            const response = await appRegisterationService.appRegistrationSubmit(payload);
            if (response?.requestNumber ) {
                setRequestData(response);
                setShowModal(true);
                resetFields();
            }else{
                toast.error(response?.errorMessage);
            }
            hideLoader();
        } catch (error) {
            hideLoader();
            toast.error(t("entraGuest.request_fail_message"), "error");
        } finally {
            hideLoader();
        }
    }

    const renderConfirmModal = () => {
        return (
            <div className="confirm-modal">
                <Modal
                    showModal={showModal}
                    className="entra-request-success-modal"
                    onCloseModal={closeModal}
                    closeOnOutsideClick={false}
                    notificationsProps={{
                        secondaryActionLabel: (
                            <>
                                <Icon
                                    alt="alt value"
                                    icon={copyButtonText == t('nonArExtension.copied') ? "check" : "content_copy"}
                                    size="medium"
                                    style={{ marginRight: '6px', display: 'unset' }}
                                />

                                {copyButtonText}
                            </>
                        ),
                        secondaryActionOnClick: onClickCopynumber,
                        text: `${requestData?.requestNumber}`,
                        title: t('nonArExtension.request_number_title'),
                        type: 'info'
                    }}
                    showAuxiliaryButton={false}
                    showLink={false}
                    showPrimaryButton={true}
                    primaryButtonProps={{
                        onClick: onViewRequest,
                        size: 'medium',
                        text: t('homePage.view_request'),
                        variant: 'primary'
                    }}
                    showSecondaryButton={false}
                    showSlot
                    showTertiaryButton={true}
                    tertiaryButtonProps={{
                        onClick: () => navigate("/home"),
                        size: 'medium',
                        text: t('nonArExtension.back'),
                        variant: 'tertiary'
                    }}
                    showCloseButton={true}
                    style={{}}
                    text={<>
                        <div style={{ marginTop: '10px' }}>
                            <p className="modal-para-text">{t('app_registeration.success_modal_sub_heading')}
                            </p>
                        </div>
                    </>}
                    title={t('app_registeration.success_modal_heading')}
                    type="fluid"
                    viewPort={`${isMobile ? "mobile" : "desktop"}`}
                >
                    <p style={{ fontSize: '16px' }} className="modal-para-text">{`${t('entraGuest.see_all_your_requests_in_the')} `} <span className="view-request-txt">{`${t('entraGuest.view_request')} `}</span> {t('entraGuest.section_below')}
                    </p>
                </Modal>
            </div>
        )
    }
    return (
        <>
            <div className="app-registeration-container entra-guest-main-container">
                <div
                    className={isMobile ? "row" : "container"}
                    style={{
                        background: "none",
                        boxShadow: "none",
                        justifyContent: isMobile ? "center" : "",
                    }}
                >
                    <div
                        style={{
                            width: "100%",
                            flexDirection: isMobile ? "column" : "row",
                            gap: isMobile ? "0px" : "15px",
                            flexWrap: "wrap",
                            flex: 1,
                        }}
                    >
                        <div className={isMobile ? "row mt-2" : ""} style={{ justifyContent: isMobile ? "center" : "" }}>
                            <div
                                className={`${isMobile ? 'pl-4 pr-4 ml-4 mr-4' : ''} ad-main-heading-container`}
                            >
                                <Breadcrumb
                                    homeIcon={{
                                        icon: "home",
                                        onClick: () => {
                                            navigate("/home")
                                        }
                                    }}
                                    breadcrumbs={[
                                        {
                                            label: "Home",
                                        },
                                        {
                                            label: t("app_registeration.application_registration"),
                                        },
                                    ]}
                                    className="entra-breadcrumb"
                                    useAnchor
                                />

                                <div className="ad-page-header">
                                    <div className="ad-main-heading">
                                        {t("app_registeration.application_registration")}
                                    </div>

                                    <div className="ad-main-desc">
                                        {t("app_registeration.register_an_application")}
                                    </div>
                                </div>
                            </div>
                            <div
                                className="card-layout mb-3 mb-md-0 entra-guest-container"
                            >
                                <div
                                    className="w-100 mb-3 mb-md-0 idx-container"
                                >
                                    <div className="w-100">
                                        <div className="card card-content align-items-center">
                                            <div className={`bulk-upload-section ${isMobile ? "" : "w-75"}`}>
                                                <div className="row">
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <TextInput
                                                            name="applicationName"
                                                            type="text"
                                                            value={applicationName}
                                                            label={t("app_retirement.application_name")}
                                                            placeholderText={t("app_retirement.type_here_placeholder")}
                                                            onKeyDown={(e) => {
                                                                const notAllowed = /(<|>|&|;|%)/g;
                                                                const k = e.key;
                                                                if (k.length === 1 && notAllowed.test(k)) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                setApplicationName(e.target.value);
                                                            }}
                                                            required={true}
                                                            maxLength={120}
                                                            helperText={t("app_registeration.application_name_info")}
                                                        />
                                                    </div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <Dropdown
                                                            className=""
                                                            childList={environments}
                                                            label={t("app_registeration.environment")}
                                                            placeholder={t("RequestGPIDForNonEmployee.select_btn_label")}
                                                            menuPosition="bottom-right"
                                                            isClearable={true}
                                                            selection="single"
                                                            size="medium"
                                                            setSelectedValue={(selected) => {
                                                                setEnvironment(selected);
                                                                selectedEnvironmentRef.current = selected;
                                                            }}
                                                            selectedValue={environment}
                                                            onKeyDown={(e) => e.preventDefault()}
                                                            showLabelIcon={false}
                                                            isRequired
                                                        />
                                                    </div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <div className="form-field app-name-search">
                                                            <div className="mb-2" style={{ display: 'flex', justifyContent: "space-between" }}>
                                                                <label className="safe-name" style={{ display: "flex", alignItems: "center" }}>
                                                                    {t("app_registeration.business_owner")}
                                                                </label>
                                                                <label className="required-text $font-xsmall-regular" style={{ color: "var(--color-text-secondary)" }}>*Required</label>
                                                            </div>
                                                            {/* <Search
                                                                className="create-safe-app-name"
                                                                isAutoComplete
                                                                isClearable={searchedBusinessOwnerQuery?.length > 0}
                                                                required
                                                                isRequired
                                                                maxLength={20}
                                                                isDisabled={disableBusinessOwnerSearch}
                                                                onClear={() => {
                                                                    setSelectedBusinessOwner(null);
                                                                    setBusinessOwners([]);
                                                                }}
                                                                onSelectOption={(val) => {
                                                                    const sel = Array.isArray(val) ? val[0] : val;
                                                                    setSelectedBusinessOwner(sel);
                                                                }}
                                                                onSearch={(val) => searchBusinessOwners(val)}
                                                                onUpdate={(text) => {
                                                                    setSearchedBusinessOwnerQuery(text);
                                                                    debouncedSearchBusinessOwners(text);
                                                                    setSelectedBusinessOwner(null);
                                                                }}
                                                                onBlur={()=>{
                                                                    setSearchedBusinessOwnerQuery("");
                                                                    setBusinessOwners([]);
                                                                }}
                                                                placeholderText={t("app_registeration.search_for_gpid")}
                                                                size="medium"
                                                                autoComplete="off"
                                                                suggestions={businessOwners}
                                                                selectedValue={selectedBusinessOwner}
                                                                limitSuggestions={businessOwners?.length}
                                                                searchTerm={searchedBusinessOwnerQuery}
                                                                delay={0}
                                                            /> */}
                                                            <Search
                                                                className="create-safe-app-name"
                                                                isAutoComplete
                                                                isClearable={searchedBusinessOwnerQuery?.length > 0}
                                                                onClear={() => { clearBusinessUsers(); setSearchedBusinessOwnerQuery(""); }}
                                                                maxLength={30}
                                                                value={searchedBusinessOwnerQuery}
                                                                isDisabled={disableBusinessOwnerSearch || businessOwner.length >0}
                                                                onSelectOption={(val) => {
                                                                    addToBusinessOwners(val);
                                                                    setSearchedBusinessOwnerQuery("");
                                                                    setBusinessOwners([]);
                                                                }}
                                                                onSearch={(event) => searchTechnicalOwners(event)}
                                                                onUpdate={(text) => {
                                                                    setSearchedBusinessOwnerQuery(text);
                                                                    debouncedSearchBusinessOwners(text);
                                                                }}
                                                                // onBlur={()=>{
                                                                //     setSearchedBusinessOwnerQuery("");
                                                                //     setBusinessOwners([]);
                                                                // }}
                                                                placeholderText={t("app_registeration.search_for_gpid")}
                                                                size="medium"
                                                                style={{}}
                                                                autoComplete="off"
                                                                suggestions={businessOwners}
                                                                delay={0}
                                                                limitSuggestions={businessOwners.length}
                                                            />
                                                            {businessOwner.length > 0 && <div>
                                                                <div className="accountsAlign">
                                                                    {businessOwner?.map((acc, index) => (
                                                                        <span key={acc?.id} className="endPointInfo mr-1">{acc?.id}{" "}
                                                                            <Icon
                                                                                alt="alt value"
                                                                                className="icon $color-icon small material-icons-outlined ml-1"
                                                                                icon="close"
                                                                                size="small"
                                                                                onClick={() => setBusinessOwner((prev)=>prev?.filter?.((x)=>x?.id!==acc?.id))}
                                                                                style={{ color: 'white', cursor: "pointer" }}
                                                                            />
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            }
                                                        </div>
                                                    </div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6"}></div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <div className="form-field mb-3 app-name-search">
                                                            <div className="mb-2" style={{ display: 'flex', justifyContent: "space-between" }}>
                                                                <label className="safe-name" style={{ display: "flex", alignItems: "center" }}>
                                                                    {t("app_registeration.technical_owner")}
                                                                </label>
                                                                <label className="required-text $font-xsmall-regular" style={{ color: "var(--color-text-secondary)" }}>*Required</label>
                                                            </div>
                                                            <Search
                                                                className="create-safe-app-name"
                                                                isAutoComplete
                                                                isClearable={searchedTechnicalOwnerQuery?.length > 0}
                                                                onClear={() => { clearUsers(); setSearchedTechnicalOwnerQuery(""); }}
                                                                maxLength={30}
                                                                value={searchedTechnicalOwnerQuery}
                                                                isDisabled={disableTechnicalOwnerSearch || technicalOwners.length >= 5}
                                                                onSelectOption={(val) => {
                                                                    addToTechnicalOwners(val);
                                                                    setSearchedTechnicalOwnerQuery("");
                                                                    setUsers([]);
                                                                }}
                                                                onSearch={(event) => searchTechnicalOwners(event)}
                                                                onUpdate={(text) => {
                                                                    setSearchedTechnicalOwnerQuery(text);
                                                                    debouncedSearchTechnicalOwners(text);
                                                                }}
                                                                // onBlur={()=>{
                                                                //     setSearchedTechnicalOwnerQuery("");
                                                                //     setUsers([]);
                                                                // }}
                                                                placeholderText={t("app_registeration.search_for_gpid")}
                                                                size="medium"
                                                                style={{}}
                                                                autoComplete="off"
                                                                suggestions={users}
                                                                delay={0}
                                                                limitSuggestions={users.length}
                                                            />
                                                            {technicalOwners.length > 0 && <div>
                                                                <div className="accountsAlign">
                                                                    {technicalOwners?.map((acc, index) => (
                                                                        <span key={acc?.id} className="endPointInfo mr-1">{acc?.id}{" "}
                                                                            <Icon
                                                                                alt="alt value"
                                                                                className="icon $color-icon small material-icons-outlined ml-1"
                                                                                icon="close"
                                                                                size="small"
                                                                                onClick={() => setTechnicalOwners((prev)=>prev?.filter?.((x)=>x?.id!==acc?.id))}
                                                                                style={{ color: 'white', cursor: "pointer" }}
                                                                            />
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                            }
                                                            <div className={`${technicalOwners.length === 1 ? "error" : ""} feedback $font-xsmall-regular info dropdown-hint $spacing-mt03 medium`} data-testid="feedback"><span className="icon $color-icon small material-icons-outlined" role="img" aria-label="info" style={{fontSize: "16px", marginRight: "0.25rem"}}>info</span>{t("app_registeration.minimum_2_and_maximum_5_members_can_be_added")}</div>
                                                        </div>
                                                    </div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <TextInput
                                                            name="supportGroup"
                                                            type="text"
                                                            value={supportGroup}
                                                            label={t("app_registeration.application_owner_support_group")}
                                                            placeholderText={t("app_registeration.support_group_placeholder")}
                                                            onKeyDown={(e) => {
                                                                const allowed = /[^A-Za-z 0-9_-]/g;
                                                                const k = e.key;
                                                                if (k.length === 1 && allowed.test(k)) {
                                                                    e.preventDefault();
                                                                }
                                                            }}
                                                            onChange={(e) => {
                                                                setSupportGroup(e.target.value);
                                                            }}
                                                            required={true}
                                                        />
                                                    </div>
                                                    {/* <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <Dropdown
                                                            className=""
                                                            childList={applicationTypes}
                                                            label={t("app_registeration.application_type")}
                                                            placeholder={t("app_registeration.select")}
                                                            menuPosition="bottom-right"
                                                            isClearable={true}
                                                            selection="single"
                                                            size="medium"
                                                            setSelectedValue={(selected) => setApplicationType(selected)}
                                                            selectedValue={applicationType}
                                                            onKeyDown={(e) => e.preventDefault()}
                                                            showLabelIcon={false}
                                                            isRequired
                                                        />
                                                    </div> */}
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <Dropdown
                                                            className=""
                                                            childList={signInAudiences}
                                                            label={t("app_registeration.sign_in_audience")}
                                                            placeholder={t("app_registeration.select")}
                                                            menuPosition="bottom-right"
                                                            isClearable={true}
                                                            selection="single"
                                                            size="medium"
                                                            setSelectedValue={(selected) => {
                                                                if(selected?.[0]?.id === "PersonalMicrosoftAccount"){
                                                                    setApiPermissionsIndex(1);
                                                                }
                                                                setSignInAudience(selected);
                                                                selectedSignInAudienceRef.current = selected;
                                                            }}
                                                            selectedValue={signInAudience}
                                                            onKeyDown={(e) => e.preventDefault()}
                                                            showLabelIcon={false}
                                                            isRequired
                                                            helperText={t("app_registeration.sign_in_audience_info")}
                                                        />
                                                    </div>
                                                    <div className={isMobile ? `${signInAudience?.[0]?.id === "PersonalMicrosoftAccount" ? "disabled" : ""} col-12` : `${signInAudience?.[0]?.id === "PersonalMicrosoftAccount" ? "disabled" : ""} col-12 col-md-6 mb-3`}>
                                                        <Radio
                                                            altText=""
                                                            className="mb-3"
                                                            feedbackType="info"
                                                            items={[
                                                                'Yes',
                                                                'No'
                                                            ]}
                                                            label={t("app_registeration.api_permissions")}
                                                            orientation="horizontal"
                                                            reference="radio button"
                                                            showText
                                                            size="small"
                                                            style={{}}
                                                            required={true}
                                                            selectedItemIndex={apiPermissionsIndex}
                                                            onUpdate={(index, value) => {
                                                                setApiPermissionsIndex(index);
                                                            }}
                                                            disabled={signInAudience?.[0]?.id === "PersonalMicrosoftAccount"}
                                                        />
                                                        {
                                                            apiPermissionsIndex === 0 ?
                                                                <div className="certificate-details-section mb-3">
                                                                    <Dropdown
                                                                        className=""
                                                                        childList={requestedApiPermissions}
                                                                        label={t("app_registeration.requested_api_permissions")}
                                                                        placeholder={t("app_registeration.select")}
                                                                        menuPosition="bottom-right"
                                                                        isClearable={true}
                                                                        selection="multiple"
                                                                        size="medium"
                                                                        setSelectedValue={(selected) => setRequestedApiPermission(selected)}
                                                                        selectedValue={requestedApiPermission}
                                                                        onKeyDown={(e) => e.preventDefault()}
                                                                        showLabelIcon={false}
                                                                        isRequired
                                                                    />
                                                                    <div className="feedback $font-xsmall-regular info dropdown-hint $spacing-mt03 medium d-flex" data-testid="feedback"><span className="icon $color-icon small material-icons-outlined" role="img" aria-label="info" style={{fontSize: "16px", marginRight: "0.25rem"}}>info</span><div>{t("app_registeration.api_permissions_info")}<strong className="entra_id_service_request-link ml-1" onClick={()=>{
                                                                            window.open(
                                                                                "https://pepsico.service-now.com/mypepsico?id=sc_cat_item&table=sc_cat_item&sys_id=df3d43cb47220a50bb2ef564116d4322",
                                                                                "_blank"
                                                                            );
                                                                        }}>{t("app_registeration.entra_id_service_request")}</strong></div></div>
                                                                </div>
                                                                : null
                                                        }
                                                    </div>
                                                    <div className={isMobile ? "col-12" : "col-12 col-md-6 mb-3"}>
                                                        <TextArea
                                                            title={t("app_registeration.remarks_business_justification")}
                                                            label={t("app_registeration.remarks_business_justification")}
                                                            placeholder={t("app_registeration.typed_text_goes_here")}
                                                            rows={3}
                                                            style={{ width: "100%"}}
                                                            onChange={(e) => setBusinessPurpose(e.target.value)}
                                                            value={businessPurpose}
                                                            showToolbar={false}
                                                            required={true}
                                                            showCharacterCount={true}
                                                            maxLength={200}
                                                            showLabelIcon={false}
                                                            disableResize={true}
                                                            className="mw-100"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="entra-guest-footer app-registeration-footer mt-3">
                <div className="row footer-row-with-footer-buttons">
                    <Checkbox
                        className=""
                        feedbackType="info"
                        helperText=""
                        size="small"
                        style={{}}
                        checked ={isConfirmed}
                        onUpdate={(event) => {
                            setIsConfirmed(event);
                        }}
                        text={t("app_registeration.acknowledge_text")}
                    />
                    <div className="footer-buttons">
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0 px-md-0 justify-content-center">
                            <Button
                                className="search-user-cancel-button mr-md-2 my-0"
                                type="button"
                                size="medium"
                                text={t("RequestGPIDForNonEmployee.cancel_btn_label")}
                                variant="tertiary"
                                onClick={() => navigate("/home")}
                            />
                        </div>
                        <div className="col-md-auto align-self-center d-flex mb-3 mb-md-0">
                            <Button
                                className="search-user-select-button request-button my-0 px-3 justify-content-center"
                                type="button"
                                size="medium"
                                text={t("nonArExtension.submit")}
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={disableSubmit()}
                            />
                        </div>
                    </div>
                </div>
                <ToastContainer
                    position="bottom-center"
                />
                {showModal && renderConfirmModal()}
            </div>
        </>
    )
}

export default AppRegisteration;